import type { ResponseItem } from 'services/assist-api/entities/test-instance-response/types';
import type { TestItemWithRubricItems } from 'services/assist-api/entities/test-item/test-items';
import type { TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';

export type UiQuestion = {
  id: number;
  name: string;
  description: string;
  responseItems: ResponseItem[];
  rubricItems: TestRubricItemWithOptions[];
};

/**
 * A constructed category that is displayed in the UI. It represents either the question's category
 * (that has no children) or, for orphaned questions, is constructed from the question itself.
 */
export type UiCategory = {
  id: `uiCategory-category-${number}` | `uiCategory-testItem-${number}`; // Either category or orphaned test item ID.
  name: string;
  questions: UiQuestion[];
  isOrphanQuestionCategory: boolean;
};

/**
 * For CIPS Writing we currently only want to only display questions that are linked to a test item
 * rubric. These should all have a type of "multichoice" in practice.
 */
function buildRubricItems(
  testItem: TestItemWithRubricItems,
  allRubricItems: TestRubricItemWithOptions[]
): TestRubricItemWithOptions[] {
  return allRubricItems.filter((rubricItem: TestRubricItemWithOptions) =>
    testItem.testRubricItems?.find((testRubricItem) => testRubricItem.id === rubricItem.id)
  );
}

function buildResponseItems(
  testItem: TestItemWithRubricItems,
  allResponseItems: ResponseItem[]
): ResponseItem[] {
  return allResponseItems.filter(
    (responseItem: ResponseItem) => responseItem.testItem.id === testItem.id
  );
}

function buildQuestion(
  testItem: TestItemWithRubricItems,
  allResponseItems: ResponseItem[],
  allRubricItems: TestRubricItemWithOptions[]
): UiQuestion {
  return {
    id: testItem.id,
    name: testItem.name,
    description: testItem.description,
    responseItems: buildResponseItems(testItem, allResponseItems),
    rubricItems: buildRubricItems(testItem, allRubricItems)
  };
}

function buildCategoryQuestions(
  testItemsInCategory: TestItemWithRubricItems[],
  allResponseItems: ResponseItem[],
  allRubricItems: TestRubricItemWithOptions[]
): UiQuestion[] {
  return testItemsInCategory.map((testItem: TestItemWithRubricItems) =>
    buildQuestion(testItem, allResponseItems, allRubricItems)
  );
}

/**
 * Returns only UI categories containing questions that can be marked. Here we are making the
 * assertion that questions without rubric items are not questions that can be marked; therefore
 * these questions and their categories should not be rendered in the UI.
 */
function uiCategoriesWithRubricItems(uiCategories: UiCategory[]): UiCategory[] {
  return uiCategories.filter((c) => c.questions.filter((q) => q.rubricItems.length > 0).length > 0);
}

/**
 * Builds the UI question categories that appear on Manage Marking and Mark Response pages based on
 * data returned from shared marking hooks. These categories should only contain questions that can
 * be marked, so empty categories or questions not attached to response items with associated rubric
 * items should be omitted.
 */
export default function buildUiCategories(
  allTestItems: TestItemWithRubricItems[],
  allResponseItems: ResponseItem[],
  allRubricItems: TestRubricItemWithOptions[]
): UiCategory[] {
  const categories = allTestItems
    .flatMap((testItem) => testItem?.categories || [])
    .filter((c) => !c.children?.length)
    .sort((a, b) => a.order - b.order); // Todo: Convert to toSorted after Node 20 upgrade
  const orphanedQuestions = allTestItems
    .filter((testItem) => !testItem?.categories)
    .sort((a, b) => a.order - b.order); // Todo: Convert to toSorted after Node 20 upgrade

  const uiCategories: UiCategory[] = categories.map((category) => {
    const testItemsInCategory = allTestItems.filter((ti) =>
      // No question category (orphaned question) === undefined
      ti.categories?.flatMap((c) => c.id).includes(category.id)
    );
    return {
      id: `uiCategory-category-${category.id}`,
      name: category.name,
      questions: buildCategoryQuestions(testItemsInCategory, allResponseItems, allRubricItems),
      isOrphanQuestionCategory: false
    };
  });
  const uiOrphanQuestionCategories: UiCategory[] = orphanedQuestions.map((testItem) => ({
    id: `uiCategory-testItem-${testItem.id}`,
    name: testItem.name,
    questions: [buildQuestion(testItem, allResponseItems, allRubricItems)],
    isOrphanQuestionCategory: true
  }));

  const combinedUICategories = uiCategories.concat(uiOrphanQuestionCategories);
  return uiCategoriesWithRubricItems(combinedUICategories);
}
